class Apii {
    constructor() {}
    allowedMethods = ['get', 'post', 'put', 'delete'];
    baseUrl = '/api';

    // Cached promise for the one-per-session CSRF cookie fetch (see ensureCsrfCookie).
    csrfPromise = null;

    setHeaders(headers) {
        // Auth rides on the Sanctum session cookie + XSRF token, not a bearer
        // token from localStorage (SUPPORT-663 / F-024). Only merge any
        // explicitly-passed headers here.
        for (const key in headers) {
            window.axios.defaults.headers.common[key] = headers[key];
        }
    }

    prepareMethods(method) {
        let actualMethod = 'post';
        method = method.toLowerCase();
        if (!this.allowedMethods.includes(method)) {
            const error = new Error('Method not allowed.');
            return error;
        }

        switch (method) {
            case 'get':
                actualMethod = 'get';
                break;
            case 'post':
            case 'put':
            case 'delete':
                break;
        }
        return {
            expected: method,
            actual: actualMethod,
        };
    }

    /**
     * Fetch the Sanctum CSRF cookie at most once per session.
     *
     * axios is configured with `withCredentials` + `withXSRFToken`
     * (resources/js/bootstrap.js), so once the XSRF-TOKEN cookie is set it is
     * sent as the X-XSRF-TOKEN header on every subsequent request. Re-fetching
     * before each mutation (the previous behaviour) doubled request volume and
     * was a primary driver of the shared rate-limit exhaustion in SUPPORT-594.
     * Pass `force = true` to refresh after a 419 (expired token).
     */
    ensureCsrfCookie(force = false) {
        if (force) {
            this.csrfPromise = null;
        }
        if (!this.csrfPromise) {
            this.csrfPromise = window.axios
                .get('/sanctum/csrf-cookie')
                .catch((error) => {
                    // Don't cache a failed fetch — let the next call retry.
                    this.csrfPromise = null;
                    throw error;
                });
        }
        return this.csrfPromise;
    }

    /**
     * Send a request with the CSRF handling shared by apiCall and uploadFile.
     *
     * For mutations (`needsCsrf`), the CSRF cookie is ensured first and an
     * expired token (419, e.g. a long-idle session) triggers a one-shot cookie
     * refresh + single replay so the write isn't lost (SUPPORT-594). GETs skip
     * both. The retry calls `send` directly, so it can fire at most once.
     *
     * @param {() => Promise} send - Issues the underlying axios request.
     * @param {boolean} needsCsrf - Whether this is a CSRF-protected mutation.
     */
    async sendWithCsrf(send, needsCsrf) {
        if (!needsCsrf) {
            return send();
        }
        await this.ensureCsrfCookie();
        try {
            return await send();
        } catch (error) {
            if (error?.response?.status === 419) {
                await this.ensureCsrfCookie(true);
                return await send();
            }
            throw error;
        }
    }

    /**
     * Makes calls to the api
     * @param {string} route - The api resource request, users, users/{id}
     * @param {string} method - Http method, get, post, put, delete
     * @param {object} data - Data passed to the resource, { name: 'Bob' }
     * @param {object} headers - Additional headers, { 'Accept', 'application/json' }
     */
    /**
     * Uploads one or multiple files (and optional additional fields) to the given API endpoint using FormData.
     * Handles CSRF and authentication like apiCall.
     * @param {string} route - The API endpoint (e.g., '/upload')
     * @param {File|File[]} fileOrFiles - Single File or array of Files
     * @param {object} fields - Optional. Additional fields to append to FormData
     * @param {object} headers - Optional. Additional headers
     * @param {string} method - Optional. HTTP method, default 'post'
     * @returns {Promise} Axios response promise
     */
    async uploadFile(
        route,
        fileOrFiles,
        fields = {},
        headers = {},
        method = 'post'
    ) {
        this.setHeaders(headers);
        const methods = this.prepareMethods(method);
        const formData = new FormData();
        if (Array.isArray(fileOrFiles)) {
            fileOrFiles.forEach((file, idx) => {
                formData.append('files[]', file);
            });
        } else if (fileOrFiles) {
            formData.append('file', fileOrFiles);
        }
        if (fields && typeof fields === 'object') {
            Object.keys(fields).forEach((key) => {
                formData.append(key, fields[key]);
            });
        }
        return this.sendWithCsrf(
            () => window.axios[methods.actual](this.baseUrl + route, formData),
            methods.actual === 'post' || methods.actual === 'put'
        );
    }

    async apiCall(route = '/', method = 'post', data = {}, headers = {}) {
        this.setHeaders(headers);
        const methods = this.prepareMethods(method);
        if (method !== 'post' && method !== 'get') {
            data._method = methods.expected;
        }

        const payload = methods.actual === 'get' ? { params: data } : data;

        // Mutations need the CSRF cookie (once per session) and a one-shot
        // refresh + retry if the token has expired (SUPPORT-594).
        return this.sendWithCsrf(
            () => window.axios[methods.actual](this.baseUrl + route, payload),
            methods.actual === 'post'
        );
    }
}
export { Apii };
